import jsPDF from "jspdf";
import ExcelJS from "exceljs";

export function letterheadHtml(tenant) {
  if (!tenant) return "";
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[char]));
  const companyName = escapeHtml(tenant.company_name || tenant.trade_name || "EGS BUSINESS SUITE");
  const tradeName = escapeHtml(tenant.trade_name || "");
  const tin = escapeHtml(tenant.tin_number || "");
  const logoUrl = escapeHtml(tenant.logo_url || "/branding/egs-logo.jpg");

  return `<table role="presentation" style="width:100%;border-collapse:collapse;margin:0 0 20px;background:#0A1410;border-left:6px solid #D4A017;color:#F5F2E8;font-family:Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact"><tbody><tr><td style="width:74px;padding:12px 8px 12px 14px;vertical-align:middle"><img src="${logoUrl}" alt="${companyName} logo" style="display:block;width:58px;height:58px;object-fit:contain;background:#fff;border-radius:4px;padding:3px" /></td><td style="padding:12px 14px 12px 6px;vertical-align:middle"><div style="font-size:19px;font-weight:700;line-height:1.2;color:#fff">${companyName}</div>${tradeName ? `<div style="margin-top:3px;font-size:11px;color:#D4A017">${tradeName}</div>` : ""}${tin ? `<div style="margin-top:3px;font-size:10px;color:#D7DDD8">TIN: ${tin}</div>` : ""}</td></tr><tr><td colspan="2" style="height:3px;padding:0;background:#D4A017"></td></tr></tbody></table>`;
}

export function renderDocHtml({ tenant, title, content }) {
  const head = letterheadHtml(tenant);
  const safeTitle = String(title || "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
  const heading = safeTitle ? `<h1 style="font-family:Arial,sans-serif;font-size:18px;color:#0F766E;border-bottom:2px solid #D4A017;padding-bottom:7px;margin:0 0 14px">${safeTitle}</h1>` : "";
  return `<div style="font-family:Georgia,'Times New Roman',serif;color:#111;font-size:13px;line-height:1.6">${head}${heading}${content || ""}</div>`;
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportPDF(title, html) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.html(`<div style="width:515px;padding:4px">${html}</div>`, {
    x: 40, y: 40, width: 515, windowWidth: 600,
    callback: (d) => d.save(`${title || "document"}.pdf`),
  });
}

export function exportDOC(title, html) {
  download(createDocumentBlob(title, html), `${title || "document"}.doc`);
}

function createDocumentBlob(title, html) {
  const pre = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>";
  return new Blob(["\ufeff", pre, html, "</body></html>"], { type: "application/msword" });
}

export async function exportSheetXLSX(title, rows, columnWidths = []) {
  const workbook = createSheetWorkbook(rows, columnWidths);
  const buffer = await workbook.xlsx.writeBuffer();
  download(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${title || "sheet"}.xlsx`);
}

function createSheetWorkbook(rows, columnWidths = []) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");
  rows.forEach((row) => worksheet.addRow(row));
  if (columnWidths.length) worksheet.columns = columnWidths.map((width) => ({ width: Math.max(10, Math.round(width / 7)) }));
  return workbook;
}

export async function createDocumentAttachment({ title, type, html, rows, columnWidths }) {
  const safeTitle = String(title || (type === "sheet" ? "sheet" : "document"))
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_").trim() || "document";
  if (type === "sheet") {
    const workbook = createSheetWorkbook(rows, columnWidths);
    const buffer = await workbook.xlsx.writeBuffer();
    return {
      filename: `${safeTitle}.xlsx`,
      contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      blob: new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    };
  }
  return {
    filename: `${safeTitle}.doc`,
    contentType: "application/msword",
    blob: createDocumentBlob(safeTitle, html),
  };
}

// Props whose resolved pixel values (captured from the on-screen modal width)
// would overflow or clip inside the narrower A4 sheet. We drop them so content
// flows fluidly to the sheet width instead.
const SKIP_PROPS = new Set([
  "position", "inset", "top", "left", "right", "bottom", "transform",
  "width", "height", "min-width", "min-height", "max-width", "max-height",
  "inline-size", "block-size", "min-inline-size", "min-block-size", "max-inline-size", "max-block-size",
  "contain-intrinsic-size", "contain-intrinsic-width", "contain-intrinsic-height",
  "contain-intrinsic-inline-size", "contain-intrinsic-block-size",
  "flex", "flex-basis", "flex-grow", "flex-shrink",
  "table-layout", "white-space",
]);

// Deep-clones a rendered DOM node and inlines its computed styles so it renders
// identically inside the new-tab A4 sheet (which has no Tailwind/app CSS).
function inlineClone(src) {
  const clone = src.cloneNode(false);
  if (src.nodeType === 1) {
    const cs = window.getComputedStyle(src);
    let style = "";
    for (let i = 0; i < cs.length; i++) {
      const prop = cs[i];
      if (SKIP_PROPS.has(prop)) continue;
      let val = cs.getPropertyValue(prop);
      if (!val) continue;
      // Fixed px grid tracks (from the modal's width) overflow the sheet — make
      // them fluid while preserving the number of columns/rows.
      if ((prop === "grid-template-columns" || prop === "grid-template-rows") && val.includes("px")) {
        const tracks = val.trim().split(/\s+/).length;
        val = `repeat(${tracks}, minmax(0, 1fr))`;
      }
      style += `${prop}:${val};`;
    }
    clone.setAttribute("style", style);
    clone.removeAttribute("class");
  }
  src.childNodes.forEach((child) => clone.appendChild(inlineClone(child)));
  return clone;
}

// Renders a live modal/document element into the full A4 new-tab sheet (NO system
// print dialog) with a Portrait/Landscape toggle. Matches the Office/Documents flow.
export function printElement(title, el, orientation = "portrait") {
  if (!el) return;
  const clone = inlineClone(el);
  // The sheet in the new tab already provides the white A4 "paper". Strip the
  // cloned node's own card chrome (bg/border/shadow/rounding/padding/fixed width)
  // so it flows seamlessly into the sheet instead of rendering paper-on-paper.
  clone.style.background = "transparent";
  clone.style.backgroundColor = "transparent";
  clone.style.border = "0";
  clone.style.borderRadius = "0";
  clone.style.boxShadow = "none";
  clone.style.padding = "0";
  clone.style.margin = "0 auto";
  clone.style.width = "100%";
  clone.style.maxWidth = "100%";
  clone.style.minHeight = "auto";
  openPrintable(title, clone.outerHTML, orientation);
}

// Opens a clean, full A4 sheet in a NEW TAB for preview / download / print.
export function openPrintable(title, innerHtml, orientation = "portrait") {
  const w = window.open("", "_blank");
  if (!w) { alert("Please allow pop-ups to preview / print the document."); return; }
  const dims = orientation === "landscape"
    ? { w: "269mm", min: "182mm" } : { w: "182mm", min: "269mm" };
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title || "Document"}</title>
  <style id="pg">@page { size: A4 ${orientation}; margin: 14mm; }</style>
  <style>
    html, body { margin: 0; padding: 0; background: #e9e9ec; }
    * { box-sizing: border-box; }
    .toolbar { position: sticky; top: 0; display: flex; gap: 8px; justify-content: flex-end;
      padding: 10px 16px; background: #0A1410; flex-wrap: wrap; }
    .toolbar button { padding: 8px 16px; border: 0; border-radius: 6px; cursor: pointer;
      font: 600 13px system-ui; background: #D4A017; color: #0A1410; }
    .toolbar button.alt { background: #2a3a33; color: #fff; }
    .toolbar .seg { background:#1b2a24; color:#cfe; padding:8px 12px; border-radius:6px; }
    .toolbar .seg.on { background:#0f766e; color:#fff; }
    .sheet { background: #fff; color: #111; width: ${dims.w}; min-height: ${dims.min}; margin: 18px auto;
      padding: 16mm; box-shadow: 0 4px 24px rgba(0,0,0,.25);
      font-family: Georgia, 'Times New Roman', serif; font-size: 13px; line-height: 1.6; }
    .sheet table { border-collapse: collapse; width: 100% !important; table-layout: fixed !important; }
    .sheet td, .sheet th { border: 1px solid #888; padding: 6px 8px; white-space: normal !important;
      word-break: break-word; overflow-wrap: anywhere; vertical-align: top; }
    /* Give the first (description/account/item) column more room; amounts stay compact. */
    .sheet th:first-child, .sheet td:first-child { width: 46%; }
    .sheet img { max-width: 100%; }
    /* Hard-constrain every element so nothing can exceed the A4 sheet width. */
    .sheet, .sheet * { max-width: 100% !important; overflow-wrap: anywhere; }
    @media print {
      .toolbar { display: none; }
      html, body { background: #fff; }
      .sheet { box-shadow: none; margin: 0; width: auto; min-height: auto; padding: 0; }
    }
  </style></head>
  <body>
    <div class="toolbar">
      <button class="seg" onclick="setO('portrait')" id="btnP">Portrait</button>
      <button class="seg" onclick="setO('landscape')" id="btnL">Landscape</button>
      <button class="alt" onclick="window.close()">Close</button>
      <button onclick="window.print()">Print / Save as PDF</button>
    </div>
    <div class="sheet" id="sheet">${innerHtml}</div>
    <script>
      function setO(o){
        document.getElementById('pg').textContent='@page{size:A4 '+o+';margin:14mm;}';
        var s=document.getElementById('sheet');
        if(o==='landscape'){s.style.width='269mm';s.style.minHeight='182mm';}
        else{s.style.width='182mm';s.style.minHeight='269mm';}
        document.getElementById('btnP').className='seg'+(o==='portrait'?' on':'');
        document.getElementById('btnL').className='seg'+(o==='landscape'?' on':'');
      }
      setO('${orientation}');
    </script>
  </body></html>`);
  w.document.close();
  w.focus();
}

